import { useEffect, useRef, useState } from "react";
import { Box, Center, Loader, Modal, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import RFB from "@novnc/novnc";

interface ConsoleModalProps {
  vmid: number;
  name: string;
  opened: boolean;
  onClose: () => void;
}

/*
 * noVNC console modal. Opens a WebSocket to `/api/vms/:vmid/console`
 * (same origin, so the session cookie is sent automatically), waits for
 * the backend to send a `{type:"vnc_auth",password}` text frame after
 * it has opened the upstream PVE vncwebsocket, acks it, then hands the
 * WS to RFB. The backend WS bridge authenticates the upgrade, checks VM
 * ownership, fetches a fresh PVE ticket per session, opens the upstream
 * PVE vncwebsocket, delivers the VNC password (so RFB can satisfy the
 * qemu VNC auth challenge via `sendCredentials`), and bidi-pipes the
 * RFB bytes — see apps/backend/src/routes/console.ts.
 *
 * Auto-reconnect is intentionally NOT configured: each reconnect would
 * need a fresh PVE ticket (fetched inside the WS handler), so the
 * customer re-clicks "Console" instead. TLS note: the page is http://
 * for MVP, so the console uses ws:// (no mixed-content issue). Phase 6
 * recommends TLS + a Caddy front for any non-localhost deploy.
 *
 * (Note: the @novnc/novnc npm package ships RFB as a default ESM export
 * and bundles no CSS, so there is no stylesheet import here — the canvas
 * is sized inline.)
 */

/*
 * Map the backend's WS close codes (see console.ts: 4000/4001/4003/4029/1011)
 * to a customer-visible message. noVNC 1.7.0's `disconnect` event only
 * surfaces `{ clean }`, NOT the code/reason, so the close code is
 * captured off the raw WebSocket below and read here. For an unmapped
 * code, prefer the server's `reason` if it sent one, else the generic
 * default.
 */
function consoleCloseMessage(code: number | undefined, reason: string): string {
  switch (code) {
    case 4000:
      return "Invalid VM ID.";
    case 4001:
      return "Authentication required. Please log in again.";
    case 4003:
      return "You don't have access to this VM.";
    case 4029:
      return "Too many concurrent console sessions. Close another and try again.";
    case 1011:
      return "Proxmox is unreachable. Try again in a moment.";
    default:
      return reason.trim() ? reason : "Console disconnected.";
  }
}

export function ConsoleModal({ vmid, name, opened, onClose }: ConsoleModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<RFB | null>(null);
  const [connecting, setConnecting] = useState(true);

  useEffect(() => {
    if (!opened) return;
    setConnecting(true);

    // Defer RFB construction until Mantine's Modal portal has actually
    // mounted the container div into the DOM. The original one-shot raf
    // was enough on first open but raced on subsequent opens — Mantine
    // unmounts the modal body when `keepMounted=false` (default), and on
    // rapid reopens the raf callback can fire before the remount commits,
    // leaving containerRef.current === null and silently bailing without
    // ever opening the WS (customer saw "doesn't open until refresh").
    // We ALSO set keepMounted on the Modal below as the primary fix; this
    // polling retry is the belt-and-suspenders for any portal timing edge.
    let rfb: RFB | null = null;
    let ws: WebSocket | null = null;
    let wsCloseCode: number | undefined;
    let wsCloseReason = "";
    let cancelled = false; // React unmount / dep change before RFB built
    const pendingRafs: number[] = [];
    const TRY_DEADLINE = Date.now() + 2000; // ~120 frames at 60fps

    const start = (): void => {
      if (cancelled) return;
      const target = containerRef.current;
      if (!target) {
        if (Date.now() < TRY_DEADLINE) {
          pendingRafs.push(requestAnimationFrame(start));
        } else {
          // Modal never mounted within 2s — surface as a real error
          // instead of silently hanging on the spinner.
          notifications.show({
            color: "red",
            message: "Console failed to mount. Close and try again.",
          });
          onClose();
        }
        return;
      }
      const wsUrl = `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/api/vms/${vmid}/console`;
      // Create the WebSocket ourselves and hand it to RFB as its "raw
      // channel" (second ctor arg) so we can capture the server's close
      // code — RFB 1.7.0's `disconnect` event only exposes `{ clean }`,
      // not code/reason. RFB.attach() overwrites the `onclose` *property*
      // but leaves addEventListener listeners in place, so the close
      // listener survives and the code is read in the disconnect handler.
      ws = new WebSocket(wsUrl, ["binary"]);
      ws.binaryType = "arraybuffer";
      ws.addEventListener("close", (ev) => {
        wsCloseCode = ev.code;
        wsCloseReason = ev.reason;
      });

      // The backend sends a single text frame `{type:"vnc_auth",password}`
      // after the upstream PVE VNC WS is open, then waits for our ack
      // before starting the bidi pipe. This gate matters: without it, the
      // first binary RFB bytes from PVE would arrive at this WS while no
      // listener is attached (RFB hasn't been constructed yet) and be
      // silently dropped — browser WS message events without a listener
      // are NOT buffered/replayed. We consume the auth frame, send the
      // ack, then construct RFB so its `message` listener catches every
      // subsequent binary frame.
      const authPromise = new Promise<string>((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
          if (settled) return;
          settled = true;
          ws!.removeEventListener("message", onMessage);
          ws!.removeEventListener("close", onCloseEarly);
          ws!.removeEventListener("error", onErrorEarly);
        };
        const onMessage = (event: MessageEvent) => {
          if (settled) return;
          if (typeof event.data !== "string") {
            cleanup();
            reject(new Error("expected text auth frame from server"));
            return;
          }
          let msg: unknown;
          try {
            msg = JSON.parse(event.data);
          } catch {
            cleanup();
            reject(new Error("auth frame was not valid JSON"));
            return;
          }
          if (
            msg &&
            typeof msg === "object" &&
            (msg as { type?: unknown }).type === "vnc_auth" &&
            typeof (msg as { password?: unknown }).password === "string"
          ) {
            cleanup();
            try {
              ws!.send(JSON.stringify({ type: "vnc_auth_ack" }));
            } catch {
              /* socket may already be closing — reject below */
            }
            resolve((msg as { password: string }).password);
            return;
          }
          cleanup();
          reject(new Error("unexpected first frame from server"));
        };
        const onCloseEarly = () => {
          cleanup();
          reject(new Error("ws closed before auth frame"));
        };
        const onErrorEarly = () => {
          cleanup();
          reject(new Error("ws error before auth frame"));
        };
        ws!.addEventListener("message", onMessage);
        ws!.addEventListener("close", onCloseEarly);
        ws!.addEventListener("error", onErrorEarly);
      });

      authPromise
        .then((password) => {
          if (cancelled || !ws || ws.readyState !== WebSocket.OPEN) {
            // Component unmounted or socket closed during the auth
            // handshake — the close listener already captured the code.
            return;
          }
          rfb = new RFB(target, ws, { shared: false });
          rfbRef.current = rfb;
          rfb.scaleViewport = true;
          rfb.resizeSession = false;
          rfb.showDotCursor = true;

          rfb.addEventListener("connect", () => {
            setConnecting(false);
          });
          rfb.addEventListener("disconnect", (e) => {
            const detail = e.detail as { clean?: boolean } | undefined;
            // Only react if THIS rfb is still the active one. noVNC's
            // 'disconnect' event fires after the underlying WS actually
            // closes, which can be async — if the customer has already
            // re-opened the console (constructing a fresh rfb), a late
            // 'disconnect' from the OLD rfb must NOT close the new modal.
            // Pre-fix this manifested as: open → exit → reopen → console
            // appears for ~1s → disappears (the old rfb's late disconnect
            // fired after PVE kicked its stale upstream).
            if (rfbRef.current !== rfb) return;
            rfbRef.current = null;
            if (!detail?.clean) {
              notifications.show({
                color: "red",
                message: consoleCloseMessage(wsCloseCode, wsCloseReason),
              });
            }
            onClose();
          });
          rfb.addEventListener("securityfailure", () => {
            notifications.show({ color: "red", message: "Console authentication failed." });
          });
          rfb.addEventListener("credentialsrequired", () => {
            // PVE's qemu VNC server (gated by `generate-password=1` on
            // vncproxy) challenges for a password distinct from the
            // vncticket. The backend delivered it via the auth frame;
            // supply it now. If PVE ever stops asking, this handler is
            // simply never called.
            try {
              rfb!.sendCredentials({ password });
            } catch {
              /* RFB already torn down — disconnect will fire */
            }
          });
        })
        .catch(() => {
          // Auth-handshake failure: WS closed (or sent an unexpected
          // first frame) before RFB was constructed. The close listener
          // captured the code; surface it. If no close code (e.g.
          // unexpected frame), fall back to the generic message.
          if (cancelled) return;
          notifications.show({
            color: "red",
            message:
              wsCloseCode !== undefined
                ? consoleCloseMessage(wsCloseCode, wsCloseReason)
                : "Console connection failed.",
          });
          onClose();
        });
    };

    pendingRafs.push(requestAnimationFrame(start));

    return () => {
      cancelled = true;
      for (const id of pendingRafs) cancelAnimationFrame(id);
      pendingRafs.length = 0;
      if (rfb) {
        try {
          // noVNC's RFB public API is `disconnect()`, NOT `close()` —
          // there is no close() method, so the previous rfb.close()
          // call silently threw TypeError (swallowed by this catch),
          // the WS never closed, the backend kept the session alive
          // until PVE kicked it ~1s after the next openVncProxy, and
          // the stale rfb's late disconnect event dismissed the new
          // modal. Symptom: console appears for ~1s then disappears.
          rfb.disconnect();
        } catch {
          /* already disconnected */
        }
      } else if (ws) {
        // raf was cancelled or RFB never constructed — close the WS too.
        try {
          ws.close();
        } catch {
          /* noop */
        }
      }
      rfbRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, vmid]);

  function handleClose() {
    if (rfbRef.current) {
      try {
        // RFB.disconnect() is the public API (not .close() — see the
        // comment in the cleanup return above).
        rfbRef.current.disconnect();
      } catch {
        /* noop */
      }
      rfbRef.current = null;
    }
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Console — ${name}  (click the × to exit)`}
      fullScreen
      closeOnEscape={false}
      closeOnClickOutside={false}
      keepMounted
      styles={{ body: { height: "calc(100% - 56px)" } }}
    >
      <Box style={{ position: "relative", width: "100%", height: "100%" }}>
        {connecting && (
          <Center pos="absolute" inset={0} style={{ zIndex: 1 }}>
            <Stack align="center" gap="xs">
              <Loader />
              <Text c="dimmed" size="sm">
                Connecting to console…
              </Text>
            </Stack>
          </Center>
        )}
        <div
          ref={containerRef}
          style={{ width: "100%", height: "100%", background: "#000" }}
        />
      </Box>
    </Modal>
  );
}
